// Copyright (c) Docugami, Inc. All rights reserved.

export interface ActionItem {
    execute: () => Promise<any>;
    id: string;
}

/**
 * Runs a set of actions using LIFO semantics.  The number of concurrent actions as well as the max number
 * of actions in the stack can be configured.
 */
export class AsyncActionStack {
    private _stack: ActionItem[] = [];
    private _runningActions: Promise<any>[] = []
    private _trackedSet: Set<string> = new Set<string>();

    private _maxConcurrent: number = 1;
    private _maxSize: number = -1;
    private _runner: Promise<void> | null = null;

    constructor(maxConcurrent: number = 1, maxSize: number = -1) {
        this._maxConcurrent = maxConcurrent;
        this._maxSize = maxSize;
    }

    add(...actions: ActionItem[]) {
        actions = actions.filter(x => !this._trackedSet.has(x.id));
        if (actions.length === 0) {
            return;
        }

        actions.forEach(x => this._trackedSet.add(x.id));

        this._stack.unshift(...actions);
        if (this._maxSize !== -1 && this._stack.length > this._maxSize) {
            this._stack.splice(this._maxSize).forEach(x => this._trackedSet.delete(x.id));
        }

        if (this._runner == null) {
            this._runner = this.run();
        }
    }

    private async run(): Promise<void> {
        while (this._stack.length > 0 || this._runningActions.length > 0) {
            if (this._stack.length === 0) {
                await Promise.race(this._runningActions);
            }
            else if (this._runningActions.length < this._maxConcurrent) {
                const nextAction = this._stack.shift()!;
                this.runAction(nextAction);
            }
            else {
                await Promise.race(this._runningActions);
            }
        }

        this._runner = null;
    }

    private async runAction(action: ActionItem) {
        const promise = action.execute();

        try {
            this._runningActions.push(promise);
            await promise;
        }
        catch (error) {
        }
        finally {
            this._trackedSet.delete(action.id);

            const index = this._runningActions.indexOf(promise);
            if (index !== -1) {
                this._runningActions.splice(index, 1);
            }
        }
    }
}
